import React, { Suspense } from 'react';

import { BrowserRouter, Route, Switch, useHistory } from 'react-router-dom';

import Spinner from 'components/Spinner';
import AuthLayout from 'containers/Layouts/NewPortal/AuthLayout';
import SignInPage from 'containers/Pages/SignInPage';
import useCurrentUser from 'hooks/useCurrentUser';
import { PrivateRoute, PublicRoute } from 'utils/auth-wrappers';
import { isCacheFly } from 'utils/whiteLabeling';

import useConfirmationDialog from '../components/ConfirmationDialog/useConfirmationDialog';
import PageLeavePromptProvider from '../components/PageLeavePromptProvider';
import { safeLazy } from './helpers';

const SignInErrorPage = safeLazy(() => import('../containers/Pages/SignInErrorPage'));
const SignupPage = safeLazy(() => import('../containers/Pages/SignupPage'));
const UnauthorizedPage = safeLazy(() => import('../containers/Pages/UnauthorizedPage'));
const ForgotPassPage = safeLazy(() => import('../containers/Pages/ForgotPassPage'));
const EmailConfirmationPage = safeLazy(
  () => import('../containers/Pages/EmailConfirmationPage'),
);
const SignInFromPortalPage = safeLazy(
  () => import('../containers/Pages/SignInFromPortalPage'),
);
const AuthenticatedRoutes = safeLazy(() => import('./AuthenticatedRoutes'));

const OauthAccountActions = safeLazy(
  () => import('../containers/Pages/Oauth/AccountActions'),
);

const App = () => {
  const { user } = useCurrentUser();

  const history = useHistory<{
    from: string;
  }>();

  const redirectUrl = !history.location.state?.from?.includes('signin')
    ? history.location.state?.from
    : '';

  return (
    <div className="app-main">
      <Suspense fallback={<Spinner main />}>
        <Switch>
          <PublicRoute exact path="/unauthorized">
            <UnauthorizedPage />
          </PublicRoute>

          <Route
            path={[
              '/signin',
              '/signin/error',
              '/forgotpass',
              '/email-confirm',
              '/auth/:provider/callback',
            ]}
          >
            <AuthLayout>
              <Suspense fallback={<div />}>
                <Switch>
                  <PublicRoute
                    exact
                    path="/signin"
                    redirectTo={
                      redirectUrl ||
                      (!user?.uiPermissions?.configuration
                        ? '/app/accounts'
                        : '/app/analytics')
                    }
                  >
                    <SignInPage />
                  </PublicRoute>

                  <PublicRoute exact path="/signin/error">
                    <SignInErrorPage />
                  </PublicRoute>

                  {isCacheFly && [
                    <PublicRoute exact key="/forgotpass" path="/forgotpass">
                      <ForgotPassPage />
                    </PublicRoute>,
                    <PublicRoute exact key="/email-confirm" path="/email-confirm">
                      <EmailConfirmationPage />
                    </PublicRoute>,

                    <Route key="/auth/:provider/callback" path="/auth/:provider/callback">
                      <OauthAccountActions />
                    </Route>,
                  ]}
                </Switch>
              </Suspense>
            </AuthLayout>
          </Route>
          {isCacheFly && [
            <PublicRoute exact key="/signup" path="/signup" redirectTo="/app/services">
              <SignupPage />
            </PublicRoute>,
            <PublicRoute
              exact
              key="/signup/:campaignName"
              path="/signup/:campaignName"
              redirectTo="/app/services"
            >
              <SignupPage />
            </PublicRoute>,
          ]}
          <Route path="/portal-auth/:token/:path*" component={SignInFromPortalPage} />
          <PrivateRoute path="/">
            <AuthenticatedRoutes />
          </PrivateRoute>
        </Switch>
      </Suspense>
    </div>
  );
};

const Routes = () => {
  const { getConfirmation } = useConfirmationDialog();

  return (
    <BrowserRouter
      getUserConfirmation={async (message, callback) => {
        const confirmed = await getConfirmation({
          title: 'Attention!',
          message: message,
          confirmBtnText: 'Yes',
          cancelBtnText: 'No',
        });

        callback(!!confirmed);
      }}
    >
      <PageLeavePromptProvider>
        <App />
      </PageLeavePromptProvider>
    </BrowserRouter>
  );
};

export default Routes;
